import { FunctionComponent, useEffect, useState, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import Dialog from 'Features/Dialog/Dialog'
import { IWorkroomPost, IWorkroomUserPermissions } from 'Frontend/scripts/models/workrooms.model'
import {
  addWorkroomPostSubscription,
  deleteWorkroomPost,
  getWorkroomPost,
  hasUserSubscribedToWorkroomPost,
  removeWorkroomPostSubscription,
  updateWorkroomPost,
} from 'Frontend/scripts/services/workrooms.service'
import { Button, Form } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { convertDateAndTime } from 'Frontend/scripts/convertDates'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import WorkroomPostComments from './WorkroomPostComments'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { IHasSubscription } from 'Frontend/scripts/models/subscription.model'
import { useTranslation } from 'react-i18next'

interface WorkroomPostProps {
  workroomId: number
  postId: number
  permissions: IWorkroomUserPermissions | undefined
  callback: (_activeType: number) => void | number
}

const WorkroomPost: FunctionComponent<WorkroomPostProps> = ({ workroomId, postId, permissions, callback }) => {
  const [post, setPost] = useState<IWorkroomPost>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [mainbody, setMainbody] = useState<string>('')
  const [heading, setHeading] = useState<string>('')
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false)
  const quillRef = useRef<ReactQuill | null>(null)
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      heading: '',
      mainbody: '',
    },
  })

  const createMarkup = (body: string) => {
    return { __html: body }
  }

  const deletePost = () => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      deleteWorkroomPost(token.csrfToken, workroomId, postId).then(() => {
        setIsDeleteDialogOpen(false)
        callback(0)
      })
    })
  }

  const abortEditing = () => {
    setMainbody(post?.mainBody as string)
    setValue('mainbody', post?.mainBody as string)
    setValue('heading', post?.title as string)
    setIsEditing(false)
  }

  const submitEditedPost = (data: any) => {
    if (!data.mainbody.replace(/<(.|\n)*?>/g, '').trim()) {
      return // Prevent submission if description contains only HTML tags
    }

    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      updateWorkroomPost(token.csrfToken, workroomId, postId, data.heading, mainBodyWithCorrectHTML).then(() => {
        setMainbody(mainBodyWithCorrectHTML)
        setHeading(data.heading)
        setIsEditing(false)
      })
    })
  }

  const updateSubscription = (checked: boolean) => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      if (checked) {
        addWorkroomPostSubscription(token.csrfToken, workroomId, postId)
      } else {
        removeWorkroomPostSubscription(token.csrfToken, workroomId, postId)
      }
    })
  }

  useEffect(() => {
    if (post) {
      reset({
        heading: post.title || '',
        mainbody: post.mainBody || '',
      })
    }
  }, [post, reset])

  useEffect(() => {
    getWorkroomPost(workroomId, postId).then((postData) => {
      setPost(postData)
      setMainbody(postData.mainBody)
      setHeading(postData.title)
    })
    hasUserSubscribedToWorkroomPost(workroomId, postId).then((subscription: IHasSubscription) => {
      setIsSubscribing(subscription.hasSubscription)
    })
  }, [])

  return (
    <>
      <Button variant="link" className="p-0 mb-3" onClick={() => callback(0)}>
        <i className="fa fa-angle-left pe-1 teaser__icon--turquoise" aria-hidden="true"></i>
        {t('back-to-post-list')}
      </Button>

      <div className="livstecknet__colored-area mb-4">
        {!isEditing ? (
          <>
            <div className="thread__subscribe mb-2">
              <Form.Check
                type="switch"
                id="custom-switch"
                label={t('subscribe-on-updates')}
                checked={isSubscribing}
                onChange={(e) => {
                  updateSubscription(e.target.checked)
                  setIsSubscribing(e.target.checked)
                }}
              />
            </div>
            <h2 className="mb-3 me-2 text-break">{heading}</h2>
            {mainbody && <div className="mb-3" dangerouslySetInnerHTML={createMarkup(mainbody)}></div>}

            <div className="d-flex gap-2">
              {post?.createdByUser == null ? (
                <div className="livstecknet-thread__profile-initial-letters">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <use xlinkHref="#icon-profile" />
                  </svg>
                </div>
              ) : post.createdByUser.profileImageUrl == null ? (
                <div className="livstecknet-thread__profile-initial-letters">
                  {post?.createdByUser?.firstname?.charAt(0)}
                  {post?.createdByUser?.lastname?.charAt(0)}
                </div>
              ) : (
                <img
                  className="workrooms__profile-image"
                  src={post.createdByUser.profileImageUrl}
                  alt={`${t('image-of')} ${post?.createdByUser?.firstname} ${post?.createdByUser?.lastname}`}
                />
              )}
              <div className="align-self-center">
                {post?.createdByUser != null && (
                  <a href={post?.createdByUser?.viewProfileUrl} className="livstecknet-thread__author">
                    {post?.createdByUser?.firstname}&nbsp;
                    {post?.createdByUser?.lastname}
                  </a>
                )}
                {post?.created && <p className="livstecknet-thread__time mb-0">{convertDateAndTime(post?.created)}</p>}
              </div>
            </div>
            {(post?.hasCurrentUserAccessToDelete || post?.hasCurrentUserAccessToEdit) && (
              <div className="d-flex flex-shrink-0 gap-2 justify-content-end livstecknet-thread__handle-thread">
                {post.hasCurrentUserAccessToEdit && (
                  <Button
                    variant="outline-secondary"
                    className="gap-2 livstecknet-thread__edit-question"
                    onClick={() => setIsEditing(true)}
                  >
                    <span className="fa fa-pencil"></span>
                    {t('edit')}
                  </Button>
                )}
                {post.hasCurrentUserAccessToDelete && (
                  <Button
                    variant="dark"
                    className="gap-2 livstecknet-thread__edit-question"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <span className="fa fa-trash-o"></span>
                    {t('delete')}
                  </Button>
                )}
              </div>
            )}
            <Dialog
              isOpen={isDeleteDialogOpen}
              verificationText={t('verification-delete-post')}
              submitText={t('yes-delete-post')}
              abortText={t('abort')}
              buttonVariant="dark"
              callbackSubmit={() => deletePost()}
              callbackClose={() => setIsDeleteDialogOpen(false)}
            />
          </>
        ) : (
          <>
            <Form noValidate onSubmit={handleSubmit(submitEditedPost)} className="d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label htmlFor="inputThreadHeading">{t('heading')}</Form.Label>
                <Form.Control
                  id="inputThreadHeading"
                  type="text"
                  {...register('heading', {
                    required: t('error-add-heading'),
                    maxLength: {
                      value: 255,
                      message: t('error-text-to-long'),
                    },
                  })}
                  isInvalid={!!errors.heading}
                />
                <Form.Control.Feedback type="invalid">{errors.heading?.message}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="inputPostMainbody">{t('text')}</Form.Label>
                <Controller
                  name="mainbody"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const strippedText = value?.replace(/<(.|\n)*?>/g, '').trim()
                      return strippedText.length > 0 || t('error-description')
                    },
                  }}
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      ref={quillRef}
                      id="inputPostMainbody"
                      className="notice__quill-editor"
                      theme="snow"
                      modules={quillModules}
                      onChange={(value) => {
                        setValue('mainbody', value, { shouldValidate: true })
                        trigger('mainbody')
                      }}
                    />
                  )}
                />
                {errors.mainbody && <Form.Text className="text-danger">{errors.mainbody.message}</Form.Text>}
              </Form.Group>

              <div className="d-flex gap-3">
                <Button variant="secondary" type="submit">
                  {t('save-and-publish')}
                </Button>
                <Button variant="outline-secondary" type="button" onClick={() => abortEditing()}>
                  {t('abort')}
                </Button>
              </div>
            </Form>
          </>
        )}
      </div>

      <h2 className="livstecknet__section-headline-text">{t('answers')}</h2>
      <div className="d-flex flex-column gap-3">
        <WorkroomPostComments
          workroomId={workroomId}
          postId={postId}
          permissionToCreateCommentOnPost={permissions?.createCommentForPost as boolean}
          callback={() => {}}
        />
      </div>
    </>
  )
}
export default WorkroomPost
